// Content for the calendar's info dialog. Data, not JSX, so the wording can be asserted against the
// statuses it documents. Written for dispatchers: no column names, no "dialog". `statuses` carries
// the labels the Accounting screens show, so the two pages agree.
//
// Split by what a cell actually renders, not by which calendar layout is on. The month cell shows
// one summary per day; the week cell shows that same summary plus a card per resource. So the
// shared lists below cover the summary, and RESOURCE_CARD_* cover the cards — shown only where
// cards exist, which is the week.

import { BILLING_STATES } from './dayMetrics';

export const GUIDE_TABS = {
    PAST: 'PAST',
    UPCOMING: 'UPCOMING',
};

export const GUIDE_TAB_ORDER = [GUIDE_TABS.PAST, GUIDE_TABS.UPCOMING];

export const GUIDE_TAB_LABELS = {
    [GUIDE_TABS.PAST]: 'Past days',
    [GUIDE_TABS.UPCOMING]: 'Present & future',
};

// One entry per quarter of a past day's billing card, in the card's order, plus Cancelled for the
// orders it leaves out. `statuses` lists the invoice statuses each quarter groups, so an operator can
// match a number on the card to the invoices behind it. The grouping itself is BILLING_BUCKETS';
// statusGuide.test.js holds the two together.
export const PAST_DAY_GUIDE = [
    {
        term: 'Not invoiced',
        statuses: ['Reverted'],
        blurb: 'No bill has gone out. That covers finished work waiting on a bill, bills that were reverted, and orders that were never marked complete — those cannot be billed until someone marks them done, and opening the day says how many there are. Bills go out once a week for the week before, so the last several days sitting here is normal. A day still here two weeks on is flagged for review.',
    },
    {
        term: 'Invoiced',
        statuses: ['Draft', 'Not Paid'],
        blurb: 'A bill has been started or sent, and nobody has approved it yet.',
    },
    {
        term: 'Approved',
        statuses: ['Approved', 'Processing'],
        blurb: 'Signed off and on its way to being paid. Approved is not the same as paid, and a part-paid bill counts here too until the full amount is in.',
    },
    {
        term: 'Paid',
        statuses: ['Paid'],
        blurb: 'Paid in full. A day is done once everything on it is here.',
    },
    {
        term: 'Cancelled',
        statuses: ['Cancelled', 'Voided'],
        blurb: 'Called off, so it will never be paid. Left out of all four numbers rather than counted against the day, so a day can still finish with cancelled orders on it.',
    },
];

export const PAST_DAY_MARKERS = [
    {
        term: 'Review',
        blurb: 'Something on this day needs a look — a bill nobody has paid, orders that were never marked complete, or work that still has no bill two weeks after it ran. Work waiting on this week\u2019s billing run is not flagged.',
    },
];

// Shown above the samples. A resource-day routinely sits in several of these at once and the card
// only has room for one, so the rule it picks by has to be stated — otherwise a truck reading
// "not invoiced" while also carrying an unpaid bill looks like the calendar is wrong. That is the
// confusion the worst-wins change was made to fix.
export const PAST_CARD_INTRO =
    'A resource can be in more than one of these at once. Its card shows the worst of them, so a truck with one bill nobody has raised yet and another that is invoiced reads as not invoiced.';

// Rendered as real cards so the dialog shows the thing rather than describing it. One per state a
// resource card can be in — statusGuide.test.js asserts the list is complete, because a state with
// no entry here is usually a state with no card treatment either.
//
// Ordered green, yellow, yellow, red, red, grey. That is colour order, not severity order: the
// point of the samples is to read as a gradient so the two yellows and the two reds sit together
// and the icon is what tells each pair apart. Severity runs the other way — see
// resourceBillingState, which picks the worst of these when a resource is in several at once.
export const PAST_CARD_EXAMPLES = [
    {
        state: BILLING_STATES.PAID,
        name: 'Truck 1',
        blurb: 'Everything on this resource has been paid.',
    },
    {
        state: BILLING_STATES.PROCESSING,
        name: 'Truck 2',
        blurb: 'Approved. The bill has been signed off and is on its way to being paid.',
    },
    {
        state: BILLING_STATES.UNINVOICED,
        name: 'Truck 3',
        blurb: 'Not invoiced. The work is done but no bill has gone out yet, which is normal for the last several days.',
    },
    {
        state: BILLING_STATES.UNPAID,
        name: 'Truck 4',
        blurb: 'Invoiced. The bill has gone out and nobody has approved it yet. Worth chasing.',
    },
    {
        state: BILLING_STATES.INCOMPLETE,
        name: 'Truck 5',
        blurb: 'Orders were never completed, so nothing on it can be billed yet.',
    },
    {
        state: BILLING_STATES.NONE,
        name: 'Truck 6',
        blurb: 'Nothing on it to bill.',
    },
];

export const UPCOMING_DAY_GUIDE = [
    {
        term: 'The four bars',
        blurb: 'One for each limit — cubic feet, weight, stops and pieces. Each adds up what every resource out that day is carrying against what those same resources can take, so the bar is the whole day at a glance.',
    },
    {
        term: 'A resource with no limit set',
        blurb: 'It is left out of that bar completely, on both sides. Counting its load without its limit would measure its freight against everybody else, and push the bar past full on room nobody said they had. Hover a bar to see how many resources it covers.',
    },
    {
        term: 'A greyed-out bar',
        blurb: 'Nobody out that day has set that limit, so there is nothing to measure. Set one on a resource from the Fleet tab.',
    },
    {
        term: 'The colours',
        blurb: 'Green has room, amber is close, red is at or over the limit. Cubic feet and weight will stop you adding more orders once they are full. Stops and pieces only warn you.',
    },
    {
        term: 'The percentage on the day',
        blurb: 'The fullest of the four bars — the limit that will run out first, and the one that decides whether the next order fits.',
    },
    {
        term: 'The three numbers at the top',
        blurb: 'Resources, stops and pieces: how many resources are out that day, how many stops they make, and how many pieces they carry.',
    },
    {
        term: 'Per-resource detail',
        blurb: 'Click a day to open it in full — every resource on it against its own limits, with a switch to stop each one taking new orders that day.',
    },
];

// Only shown where the cells carry a card per resource, which is the week. The month cell has one
// summary per day and nothing here applies to it.
export const RESOURCE_CARD_GUIDE = [
    {
        term: 'The percentage on a resource',
        blurb: 'How full that one resource is. Whichever limit it is closest to — cubic feet, weight, stops or pieces — is the one shown, which is not necessarily the same limit the day is closest to.',
    },
    {
        term: 'FULL',
        blurb: 'The resource has hit its limit. Cubic feet and weight will stop you adding more orders. Stops and pieces only warn you.',
    },
    {
        term: 'No Ceilings Configured',
        blurb: 'No limits are set on this resource, so there is nothing to measure it against and no percentage to show. Set one from the Fleet tab.',
    },
    {
        term: 'A greyed-out resource',
        blurb: 'It is still working that day but is not taking new orders. Open the day to turn scheduling back on.',
    },
];
